import { useEffect } from 'react';
import { useAtom } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { execute } from '@/graphql/execute';
import { getProjectJobsQuery } from '@/queries/project.query';
import { batchByproductsQuery } from '@/queries/byproduct.query';
import { vectorExportTrackingAtom } from '@/atoms/vectorExport-progress.atom';

/**
 * Mounted once per project shell (alongside `useClipboxExportProgress`). A
 * VECTOR_EXPORT job produces a download-only byproduct (no viewable layer), so
 * success is detected by the parent job transitioning to PROCESSED. On success
 * the loading toast resolves into a success toast carrying a Download action
 * that lazily resolves the byproduct's presigned URL by `processJobId`.
 */
export function useVectorExportProgress(projectId: string) {
  const { t } = useTranslation('project');
  const [trackers, setTrackers] = useAtom(vectorExportTrackingAtom);

  const projectTrackers = trackers.filter((tr) => tr.projectId === projectId);
  const hasTrackers = projectTrackers.length > 0;

  const { data: jobsData, refetch: refetchJobs } = useQuery({
    queryKey: ['projectJobs', projectId],
    queryFn: () => execute(getProjectJobsQuery, { projectId }),
    enabled: !!projectId && hasTrackers,
  });

  useEffect(() => {
    if (!hasTrackers || !projectId) return;
    const tick = () => void refetchJobs();
    tick();
    const id = window.setInterval(tick, 2000);
    return () => window.clearInterval(id);
  }, [hasTrackers, projectId, refetchJobs]);

  useEffect(() => {
    if (!hasTrackers) return;
    const jobs = jobsData?.jobs ?? [];

    setTrackers((prev) => {
      const next = [...prev];
      let changed = false;

      for (let i = next.length - 1; i >= 0; i--) {
        const tracker = next[i];
        if (tracker.projectId !== projectId) continue;

        // VECTOR_EXPORT is the only leaf in its synthetic batch, so matching by
        // batchId (and type, defensively) is unambiguous.
        const jobItem = jobs.find((j) => j.batchId === tracker.batchId && j.type === 'VECTOR_EXPORT');
        if (!jobItem) continue;

        if (jobItem.status === 'FAILED') {
          const detail =
            typeof jobItem.errorLogs === 'string' && jobItem.errorLogs.trim().length > 0
              ? jobItem.errorLogs.trim()
              : null;
          toast.error(detail ?? t('vectorExport.failedToast', { name: tracker.sourceLayerName }), {
            id: tracker.toastId,
            duration: 6000,
          });
          next.splice(i, 1);
          changed = true;
          continue;
        }

        if (jobItem.status === 'PROCESSED') {
          const completedJobId = jobItem.id;
          toast.success(
            t('vectorExport.completedToast', { name: tracker.sourceLayerName, format: tracker.format }),
            {
              id: tracker.toastId,
              duration: 10000,
              action: {
                label: t('vectorExport.download'),
                onClick: async () => {
                  try {
                    // Resolve via the synthetic ANALYSIS batch (by batchId), not the
                    // layer — vector sublayers have no sourceAttachmentId, so
                    // layerByproducts returns nothing. Same batchId-keyed lookup the
                    // analysis-batch panel uses. Match on processJobId, else take the
                    // batch's single export byproduct.
                    const data = await execute(batchByproductsQuery, { id: tracker.batchId });
                    const bps = data.batch?.byproducts ?? [];
                    const bp = bps.find((b) => b.processJobId === completedJobId) ?? bps[0];
                    if (bp) window.open(bp.downloadUrl, '_blank');
                    else toast.error(t('vectorExport.byproductNotFound'));
                  } catch (err) {
                    toast.error(err instanceof Error ? err.message : String(err));
                  }
                },
              },
            },
          );
          next.splice(i, 1);
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [jobsData, hasTrackers, projectId, setTrackers, t]);
}
